import React from 'react';
import { Check, TrendingUp, Users, Shield, Award, Zap, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CountUp from '../components/CountUp';
import { ArrowRight } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: TrendingUp,
      title: 'Data over guesswork',
      description: 'Every decision backed by analytics and insights, not assumptions.'
    },
    {
      icon: Zap,
      title: 'Automation first',
      description: 'We build systems that scale your growth without scaling your workload.'
    },
    {
      icon: Target,
      title: 'Design that converts',
      description: 'Beautiful interfaces engineered for one purpose: driving results.'
    },
    {
      icon: Shield,
      title: 'Radical transparency',
      description: 'Full visibility into campaigns, metrics, and spending. No hidden fees.'
    },
    {
      icon: Users,
      title: 'Long-term partnership',
      description: 'We measure success by your growth, not just completed projects.'
    },
    {
      icon: Award,
      title: 'Built to scale',
      description: 'Solutions that grow with you — from startup to enterprise.'
    }
  ];

  return (
    <div className="bg-white">
      {/* Page Header */}
      <section className="py-20" style={{ backgroundColor: 'rgba(17, 170, 182, 0.05)', borderBottom: '1px solid rgba(5, 46, 108, 0.12)' }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-['Space_Grotesk'] font-bold text-5xl md:text-6xl text-[#052E6C] mb-6">
            We automate growth for ambitious businesses
          </h1>
          <p className="font-['Inter'] text-xl leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
            Based in Dubai, trusted globally. We're a team of growth hackers, automation experts, and creative strategists obsessed with one thing: your measurable success.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image Side */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                  alt="ImpacTech Digital team collaboration"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute top-8 right-8 bg-white/95 backdrop-blur-sm px-6 py-4 rounded-2xl shadow-xl" style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }}>
                  <CountUp value="8+" className="font-['Space_Grotesk'] font-bold text-4xl text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]" />
                  <p className="font-['Inter'] text-sm mt-1" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>Years in Business</p>
                </div>
              </div>
            </div>

            {/* Content Side */}
            <div className="space-y-6">
              <h2 className="font-['Space_Grotesk'] font-bold text-4xl text-[#052E6C]">
                Our mission: Your growth, automated.
              </h2>
              <p className="font-['Inter'] text-lg leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                Founded in Dubai in 2017, ImpacTech Digital emerged from a simple observation: most businesses were drowning in manual marketing work while their competitors automated their way to the top.
              </p>
              <p className="font-['Inter'] text-lg leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                We set out to level the playing field. Today, we help businesses of all sizes harness the power of intelligent automation, data-driven strategies, and cutting-edge technology to achieve predictable, scalable growth.
              </p>
              
              <div className="space-y-3 pt-4">
                {[
                  'Proven track record with 150+ successful projects',
                  '312% average increase in qualified leads',
                  '98% client satisfaction and retention rate'
                ].map((point, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(17, 170, 182, 0.12)' }}>
                      <Check className="text-[#11AAB6]" size={14} />
                    </div>
                    <span className="font-['Inter']" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-16" style={{ backgroundColor: 'rgba(17, 170, 182, 0.05)', borderTop: '1px solid rgba(5, 46, 108, 0.12)', borderBottom: '1px solid rgba(5, 46, 108, 0.12)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <CountUp value="150+" className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]" />
              <p className="font-['Inter'] mt-2" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>Happy Clients</p>
            </div>
            <div className="text-center">
              <CountUp value="8+" className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]" />
              <p className="font-['Inter'] mt-2" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>Years Experience</p>
            </div>
            <div className="text-center">
              <CountUp value="312%" className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]" />
              <p className="font-['Inter'] mt-2" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>Avg Lead Growth</p>
            </div>
            <div className="text-center">
              <CountUp value="98%" className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]" />
              <p className="font-['Inter'] mt-2" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>Satisfaction Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-[#052E6C] mb-4">
              Our values
            </h2>
            <p className="font-['Inter'] text-lg max-w-2xl mx-auto" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
              The principles that guide every project, partnership, and pixel.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <Card 
                key={index}
                className="group transition-all duration-300 hover:shadow-lg"
                style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }}
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300" style={{ backgroundColor: 'rgba(17, 170, 182, 0.12)' }}>
                    <value.icon className="text-[#11AAB6]" size={28} />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-bold text-xl text-[#052E6C] mb-3">
                    {value.title}
                  </h3>
                  <p className="font-['Inter'] leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-[#052E6C] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-white mb-6">
            Let's grow together
          </h2>
          <p className="font-['Inter'] text-lg mb-8 max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Partner with a team that's as invested in your success as you are.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              asChild
              className="bg-white text-[#052E6C] hover:bg-white/90 px-8 py-6 text-lg rounded-full font-['Inter'] font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
            >
              <a href="https://booking.impactechdigital.com" target="_blank" rel="noopener noreferrer">
                Book Free Call
                <ArrowRight className="ml-2 inline-block group-hover:translate-x-1 transition-transform" size={20} />
              </a>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-white text-white px-8 py-6 text-lg rounded-full font-['Inter'] font-medium transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Link to="/contact">
                Get in Touch
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
};

export default About;
